import { displayDateUS } from "../../utils/dateUtils";
import {
  ADMINISTRATIVE_ORIENTATION_QUESTIONS,
  MEDICARE_ORIENTATION_QUESTIONS,
  SAFETY_ORIENTATION_QUESTIONS,
  UNIVERSAL_PRECAUTIONS_QUESTIONS,
} from "../../pages/Application/postTestConstants";

/** @param {unknown} value */
function escapeHtml(value) {
  if (value == null || value === "") return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function formatDate(val) {
  if (!val) return "";
  const s = displayDateUS(val);
  return s || String(val);
}

/**
 * @param {unknown} test
 */
function mapTestAnswers(test) {
  if (!test) return null;
  const newAnswers = {};
  if (test.answers && typeof test.answers === "object") {
    Object.keys(test.answers).forEach((qId) => {
      const cell = test.answers[qId];
      newAnswers[qId] = {
        ...cell,
        answer:
          cell && cell.answer === null ? "" : (cell && cell.answer) ?? "",
      };
    });
  }
  return {
    ...test,
    answers: newAnswers,
    scoringResult: test.scoringResult || null,
  };
}

/**
 * @param {unknown} resultData
 * @param {string|number|undefined} targetUserId
 */
function extractPostTestGroup(resultData, targetUserId) {
  if (!resultData) return null;
  if (resultData.postTestGroup) return resultData.postTestGroup;
  if (resultData.items && Array.isArray(resultData.items)) {
    if (resultData.items.length === 0) return null;
    const uid = parseInt(String(targetUserId), 10);
    if (!Number.isNaN(uid)) {
      const found = resultData.items.find((item) => item.userId === uid);
      if (found) return found;
    }
    return resultData.items[0];
  }
  if (
    resultData.postTestGroups &&
    Array.isArray(resultData.postTestGroups) &&
    resultData.postTestGroups.length > 0
  ) {
    return resultData.postTestGroups[0];
  }
  if (Array.isArray(resultData) && resultData.length > 0) {
    return resultData[0];
  }
  if (resultData.id != null) return resultData;
  return null;
}

/**
 * @param {{ status?: number, result?: unknown }} response
 * @param {string|number|undefined} targetUserId
 */
export function parsePostTestFromApiResponse(response, targetUserId) {
  if (!response || response.status !== 1) return null;
  const resultData = response.result || response;
  return extractPostTestGroup(resultData, targetUserId);
}

/**
 * @param {Record<string, unknown>} group
 */
function buildOrderedTests(group) {
  const testsData = Array.isArray(group.postTests)
    ? group.postTests
    : Array.isArray(group.tests)
      ? group.tests
      : [];
  return [
    mapTestAnswers(
      testsData.find(
        (t) =>
          t &&
          typeof t === "object" &&
          /** @type {{ testType?: string }} */ (t).testType ===
            "universal_precautions",
      ),
    ) || {
      testType: "universal_precautions",
      testTitle: "Universal Precautions Post Test",
      answers: {},
      scoringResult: null,
    },
    mapTestAnswers(
      testsData.find(
        (t) =>
          t &&
          typeof t === "object" &&
          /** @type {{ testType?: string }} */ (t).testType ===
            "medicare_orientation",
      ),
    ) || {
      testType: "medicare_orientation",
      testTitle: "Medicare Orientation Post Test",
      answers: {},
      scoringResult: null,
    },
    mapTestAnswers(
      testsData.find(
        (t) =>
          t &&
          typeof t === "object" &&
          /** @type {{ testType?: string }} */ (t).testType ===
            "safety_orientation",
      ),
    ) || {
      testType: "safety_orientation",
      testTitle: "Safety Orientation Post Test",
      answers: {},
      scoringResult: null,
    },
    mapTestAnswers(
      testsData.find(
        (t) =>
          t &&
          typeof t === "object" &&
          /** @type {{ testType?: string }} */ (t).testType ===
            "administrative_orientation",
      ),
    ) || {
      testType: "administrative_orientation",
      testTitle: "Administrative Orientation Post Test",
      answers: {},
      scoringResult: null,
    },
  ];
}

/**
 * @param {unknown[]} tests
 * @param {number} testIndex
 * @param {string|number} qId
 */
function getAnswerRaw(tests, testIndex, qId) {
  const test = tests[testIndex];
  if (!test || typeof test !== "object") return "";
  const answers = /** @type {{ answers?: Record<string, { answer?: unknown }> }} */ (
    test
  ).answers;
  if (!answers || typeof answers !== "object") return "";
  const key = Object.keys(answers).find((k) => String(k) === String(qId));
  if (!key) return "";
  const cell = answers[key];
  const a = cell?.answer;
  if (a === null || a === undefined) return "";
  return String(a).trim();
}

/**
 * @param {{ id: number; text: string; type: string; options?: { value: string; label: string }[] }} q
 * @param {unknown[]} tests
 * @param {number} testIndex
 */
function questionToHtml(q, tests, testIndex) {
  const ans = getAnswerRaw(tests, testIndex, q.id);
  const ansUp = ans.toUpperCase();

  if (q.type === "true_false") {
    const tSel = ansUp === "T" || ansUp === "TRUE";
    const fSel = ansUp === "F" || ansUp === "FALSE";
    return `<div class="pt-q">
      <p class="pt-qtxt"><strong>${q.id}.</strong> ${escapeHtml(q.text)}</p>
      <div class="pt-opts">
        <span class="pt-opt">True ${tSel ? "✓" : ""}</span>
        <span class="pt-opt">False ${fSel ? "✓" : ""}</span>
      </div>
    </div>`;
  }

  if (q.type === "multiple_choice" && Array.isArray(q.options)) {
    const opts = q.options
      .map((opt) => {
        const sel = ansUp === String(opt.value).toUpperCase();
        return `<div class="pt-mc">${escapeHtml(opt.label)} ${sel ? "✓" : ""}</div>`;
      })
      .join("");
    return `<div class="pt-q">
      <p class="pt-qtxt"><strong>${q.id}.</strong> ${escapeHtml(q.text)}</p>
      <div class="pt-mclist">${opts}</div>
    </div>`;
  }

  if (q.type === "open_ended") {
    return `<div class="pt-q">
      <p class="pt-qtxt"><strong>${q.id}.</strong> ${escapeHtml(q.text)}</p>
      <div class="pt-open">${escapeHtml(ans) || "—"}</div>
    </div>`;
  }

  return "";
}

/**
 * @param {unknown[]} tests
 */
function scoringSectionHtml(tests) {
  const blocks = tests
    .map((t) => {
      if (!t || typeof t !== "object") return "";
      const tr = /** @type {{ testTitle?: string; testType?: string; scoringResult?: { score?: number; totalQuestions?: number; correctAnswers?: number; incorrectAnswers?: number; percentage?: number } }} */ (
        t
      );
      const sr = tr.scoringResult;
      if (!sr) return "";
      const title =
        tr.testTitle ||
        String(tr.testType || "")
          .replace(/_/g, " ")
          .toUpperCase() ||
        "Test";
      return `<div class="pt-scorecard">
        <h4>${escapeHtml(title)}</h4>
        <p>Score: <strong>${escapeHtml(String(sr.score ?? ""))}</strong> / ${escapeHtml(String(sr.totalQuestions ?? ""))}</p>
        <p>Correct: ${escapeHtml(String(sr.correctAnswers ?? ""))} · Incorrect: ${escapeHtml(String(sr.incorrectAnswers ?? ""))}</p>
        <p>Percentage: <strong>${escapeHtml(String(sr.percentage ?? ""))}%</strong></p>
      </div>`;
    })
    .filter(Boolean)
    .join("");
  if (!blocks) return "";
  return `<div class="pt-scoring"><h3 class="pt-h3">Scoring results</h3><div class="pt-scoregrid">${blocks}</div></div>`;
}

function signatureBlock(sig) {
  if (sig == null || sig === "") {
    return `<span class="sig-empty">—</span>`;
  }
  const s = String(sig);
  if (s.startsWith("data:")) {
    return `<img class="sig-img" src="${escapeHtml(s)}" alt="" />`;
  }
  return `<span class="sig-text">${escapeHtml(s)}</span>`;
}

const POST_TEST_SECTIONS = [
  {
    key: "universal",
    title: "Universal precautions / infection control / TB",
    questions: UNIVERSAL_PRECAUTIONS_QUESTIONS,
    testIndex: 0,
  },
  {
    key: "medicare",
    title: "Medicare orientation",
    questions: MEDICARE_ORIENTATION_QUESTIONS,
    testIndex: 1,
  },
  {
    key: "safety",
    title: "Safety orientation",
    questions: SAFETY_ORIENTATION_QUESTIONS,
    testIndex: 2,
  },
  {
    key: "admin",
    title: "Administrative orientation",
    questions: ADMINISTRATIVE_ORIENTATION_QUESTIONS,
    testIndex: 3,
  },
];

/**
 * @param {Record<string, unknown>} group
 * @param {{ companyName: string; logoUrl: string }} branding
 */
export function buildPostTestPrintHtml(group, branding) {
  const company = escapeHtml(branding.companyName || "Company");
  const logoUrl = escapeHtml(branding.logoUrl || "");
  const tests = buildOrderedTests(group);

  const applicantName = escapeHtml(
    String(group.applicantName || group.orienteeName || "").trim(),
  );
  const testDate = escapeHtml(formatDate(group.testDate));

  const es = group.employeeSignature;
  const nestedEmpSig =
    es && typeof es === "object"
      ? /** @type {{ signature?: string }} */ (es).signature
      : null;
  const applicantSig =
    group.applicantSignature || group.empSignature || nestedEmpSig;
  const applicantSignedAt = escapeHtml(
    formatDate(
      group.applicantSignedAt ||
        group.applicantSignatureDate ||
        group.empSignedAt,
    ),
  );
  const reviewedBySignedBy = escapeHtml(String(group.reviewedBySignedBy || ""));
  const reviewedBySig = group.reviewedBySignature || group.adminSignature;
  const reviewedBySignedAt = escapeHtml(
    formatDate(
      group.reviewedBySignedAt ||
        group.adminSignatureDate ||
        group.reviewedBySignatureDate,
    ),
  );

  const sectionsHtml = POST_TEST_SECTIONS.map((sec, idx) => {
    const qs = sec.questions
      .map((q) => questionToHtml(q, tests, sec.testIndex))
      .join("");
    const brk = idx > 0 ? " pt-pagebreak" : "";
    return `<section class="pt-section${brk}">
      <h2 class="pt-h2">${escapeHtml(sec.title)}</h2>
      ${qs}
    </section>`;
  }).join("");

  const scoringHtml = scoringSectionHtml(tests);

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Post test — ${applicantName || "Orientee"}</title>
  <style>
    @page { margin: 0.4in; size: letter; }
    * { box-sizing: border-box; }
    body {
      font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
      font-size: 10px;
      line-height: 1.35;
      color: #111827;
      margin: 0;
      padding: 10px;
    }
    .sheet { max-width: 880px; margin: 0 auto; }
    @media screen {
      html {
        background: linear-gradient(168deg, #cbd5e1 0%, #e2e8f0 42%, #f1f5f9 100%);
        min-height: 100%;
      }
      body {
        padding: 28px 18px 40px;
        background: transparent;
        min-height: 100vh;
      }
      .sheet {
        background: #ffffff;
        padding: 28px 32px 36px;
        border: 1px solid #e2e8f0;
        border-radius: 1px;
        box-shadow:
          0 1px 2px rgba(15, 23, 42, 0.05),
          0 24px 48px -16px rgba(15, 23, 42, 0.22);
      }
    }
    @media print {
      html { background: #fff !important; }
      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
        padding: 0 !important;
        margin: 0;
        background: #fff !important;
      }
      .sheet {
        padding: 0 !important;
        border: none !important;
        box-shadow: none !important;
        border-radius: 0 !important;
        background: #fff !important;
      }
    }
    .pt-pagebreak { page-break-before: always; }
    .top {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;
      padding-bottom: 10px;
      border-bottom: 1px solid #e5e7eb;
      margin-bottom: 14px;
    }
    .logo { height: 52px; width: auto; max-width: 200px; object-fit: contain; }
    .title-block { text-align: right; flex: 1; }
    .title-main { font-size: 15px; font-weight: 700; color: #111827; margin: 0 0 4px 0; }
    .title-sub { font-size: 12px; font-weight: 500; color: #2563eb; margin: 0; }
    .pt-meta {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
      margin-bottom: 16px;
    }
    .pt-meta-lbl { font-size: 9px; font-weight: 600; color: #6b7280; text-transform: uppercase; margin-bottom: 4px; }
    .pt-meta-val {
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 8px 10px;
      font-size: 11px;
      min-height: 34px;
    }
    .pt-h2 {
      font-size: 12px;
      font-weight: 700;
      color: #1e3a8a;
      border-bottom: 1px solid #e5e7eb;
      padding-bottom: 6px;
      margin: 14px 0 10px 0;
      text-transform: uppercase;
    }
    .pt-h3 { font-size: 12px; margin: 0 0 10px 0; color: #111827; }
    .pt-q { margin-bottom: 10px; page-break-inside: avoid; }
    .pt-qtxt { margin: 0 0 4px 0; text-align: justify; color: #374151; }
    .pt-opts { display: flex; gap: 20px; margin-left: 14px; font-size: 10px; }
    .pt-opt { font-weight: 500; }
    .pt-mclist { margin-left: 14px; }
    .pt-mc { font-size: 10px; margin-bottom: 3px; color: #374151; }
    .pt-open {
      margin: 4px 0 0 14px;
      padding: 8px 10px;
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      background: #f9fafb;
      min-height: 36px;
      white-space: pre-wrap;
      font-size: 10px;
      color: #111827;
    }
    .pt-scoring {
      margin-top: 16px;
      padding: 12px;
      background: #eff6ff;
      border: 1px solid #bfdbfe;
      border-radius: 10px;
    }
    .pt-scoregrid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px;
    }
    .pt-scorecard {
      background: #fff;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 10px;
      font-size: 10px;
    }
    .pt-scorecard h4 { margin: 0 0 6px 0; font-size: 11px; color: #1e3a8a; }
    .two-col {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 14px;
      margin-top: 18px;
    }
    .sig-card {
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 12px;
      background: #fafafa;
    }
    .sig-card h3 { font-size: 12px; font-weight: 700; margin: 0 0 10px 0; color: #111827; }
    .sig-label {
      font-size: 9px;
      font-weight: 600;
      letter-spacing: 0.06em;
      color: #6b7280;
      margin: 8px 0 4px 0;
    }
    .sig-line {
      border-bottom: 1px solid #9ca3af;
      min-height: 26px;
      font-size: 11px;
      padding: 4px 0;
    }
    .sig-img { max-height: 44px; max-width: 100%; object-fit: contain; }
    .sig-empty { color: #9ca3af; }
    .footer-print {
      margin-top: 18px;
      padding-top: 10px;
      border-top: 1px solid #e5e7eb;
      text-align: center;
      font-size: 9px;
      color: #6b7280;
    }
  </style>
</head>
<body>
  <div class="sheet">
    <div class="top">
      <img class="logo" src="${logoUrl}" alt="" onerror="this.style.display='none'" />
      <div class="title-block">
        <p class="title-main">Orientation post test</p>
        <p class="title-sub">${company}</p>
      </div>
    </div>
    <div class="pt-meta">
      <div>
        <div class="pt-meta-lbl">Clinician / orientee name</div>
        <div class="pt-meta-val">${applicantName || "—"}</div>
      </div>
      <div>
        <div class="pt-meta-lbl">Test date</div>
        <div class="pt-meta-val">${testDate || "—"}</div>
      </div>
    </div>

    ${sectionsHtml}
    ${scoringHtml}

    <div class="two-col">
      <div class="sig-card">
        <h3>Employee</h3>
        <div class="sig-label">Name</div>
        <div class="pt-meta-val" style="margin-bottom:8px;">${applicantName || "—"}</div>
        <div class="sig-label">Signature</div>
        <div class="sig-line">${signatureBlock(applicantSig)}</div>
        <div class="sig-label">Date</div>
        <div class="sig-line">${applicantSignedAt || "—"}</div>
      </div>
      <div class="sig-card">
        <h3>Admin / reviewer</h3>
        <div class="sig-label">Name</div>
        <div class="pt-meta-val" style="margin-bottom:8px;">${reviewedBySignedBy || "—"}</div>
        <div class="sig-label">Signature</div>
        <div class="sig-line">${signatureBlock(reviewedBySig)}</div>
        <div class="sig-label">Date</div>
        <div class="sig-line">${reviewedBySignedAt || "—"}</div>
      </div>
    </div>

    <div class="footer-print">${company}</div>
  </div>
</body>
</html>`;
}
